import { AccessRequestFilters } from "@/@types/access-request.types";
import {
  AccessRequestTypeEnum,
  StatusEnumAccessRequest,
} from "@/enum/access_request";
import { Paginated } from "src/@types/paginated";
import { Tags } from "../enum/tags";
import {
  AccessRequestDTO,
  CreateAccessResponse,
} from "./dtos/accessRequest.dto";
import { PendingCourse } from "./dtos/pending-course-konviva";
import {
  IUnlockApproveParams,
  IUnlockEligibleParams,
  IUnlockEligibleResponse,
  IUnlockRequestCreateParams,
  IUnlockRequestReference,
  IUnlockStoreOption,
} from "@/@types/unlock-request";
import { apiSlice } from "./http";
import { mergeEligiblePages } from "./unlock-eligible-pages";

export const requestAccess = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAccessRequest: builder.query<
      Paginated<AccessRequestDTO>,
      AccessRequestFilters
    >({
      query: (filters) => {
        const params = new URLSearchParams();
        params.set("page", filters.page.toString());
        params.set("perPage", filters.perPage.toString());

        if (filters.search) params.set("search", filters.search);
        if (filters.store_code) params.set("store_code", filters.store_code);
        if (filters.from_date) params.set("from_date", filters.from_date);
        if (filters.to_date) params.set("to_date", filters.to_date);
        if (filters.status) params.set("status", filters.status);
        if (filters.type) params.set("type", filters.type);

        if (filters.orderBy && Object.keys(filters.orderBy).length > 0) {
          params.set("orderBy", JSON.stringify(filters.orderBy));
        } else {
          params.set("orderBy", "{}");
        }
        const url = `/access-request?${params.toString()}`;

        return {
          url,
          method: "GET",
        };
      },
      providesTags: [Tags.ACCESS_REQUEST],
      keepUnusedDataFor: 0,

      // Normaliza valores vazios para evitar cache keys duplicadas (undefined → "")
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const serialized = JSON.stringify({
          endpointName,
          page: queryArgs.page,
          perPage: queryArgs.perPage,
          search: queryArgs.search || "",
          store_code: queryArgs.store_code || "",
          from_date: queryArgs.from_date || "",
          to_date: queryArgs.to_date || "",
          status: queryArgs.status || "",
          type: queryArgs.type || AccessRequestTypeEnum.ACCESS,
          orderBy: queryArgs.orderBy || {},
        });
        return serialized;
      },
    }),
    getById: builder.query<AccessRequestDTO, string>({
      query: (id) => {
        return {
          url: `/access-request/${id}`,
          method: "GET",
        };
      },
      providesTags: [Tags.ACCESS_REQUEST],
    }),
    createRequest: builder.mutation<
      string,
      { name: string; cpf: string; store: string }
    >({
      query: (body) => ({
        url: `/access-request`,
        method: "POST",
        body,
      }),
      invalidatesTags: [Tags.ACCESS_REQUEST],
    }),
    updatedRequest: builder.mutation<
      string,
      {
        comment: string;
        id: string;
        status: StatusEnumAccessRequest;
        access_details?: CreateAccessResponse;
      }
    >({
      query: (body) => ({
        url: `/access-request/${body.id}/history`,
        method: "POST",
        body,
      }),
      invalidatesTags: [Tags.ACCESS_REQUEST],
    }),
    finishRequest: builder.mutation<string, string>({
      query: (id) => ({
        url: `/access-request/${id}/finish`,
        method: "POST",
      }),
      invalidatesTags: [Tags.ACCESS_REQUEST],
    }),
    getUnlockStores: builder.query<IUnlockStoreOption[], void>({
      query: () => ({
        url: `/access-request/unlock/stores`,
        method: "GET",
      }),
    }),
    getUnlockEligible: builder.query<
      IUnlockEligibleResponse,
      IUnlockEligibleParams
    >({
      query: ({ entityType, storeCode, search, page, limit }) => {
        const params = new URLSearchParams();
        params.set("entityType", entityType);
        if (storeCode != null) params.set("storeCode", String(storeCode));
        if (search) params.set("search", search);
        if (page != null) params.set("page", String(page));
        if (limit != null) params.set("limit", String(limit));

        return {
          url: `/access-request/unlock/eligible?${params.toString()}`,
          method: "GET",
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        JSON.stringify({
          endpointName,
          entityType: queryArgs.entityType,
          storeCode: queryArgs.storeCode,
          search: queryArgs.search || "",
          limit: queryArgs.limit,
        }),
      merge: (cached, incoming, { arg }) =>
        mergeEligiblePages(cached, incoming, arg.page),
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page,
      providesTags: [Tags.UNLOCK_ELIGIBLE],
    }),
    createUnlockRequest: builder.mutation<
      IUnlockRequestReference,
      IUnlockRequestCreateParams
    >({
      query: (body) => ({
        url: `/access-request/unlock`,
        method: "POST",
        body,
      }),
      invalidatesTags: [Tags.UNLOCK_ELIGIBLE, Tags.ACCESS_REQUEST],
    }),
    approveUnlockRequest: builder.mutation<
      IUnlockRequestReference,
      IUnlockApproveParams
    >({
      query: ({ id, ...body }) => ({
        url: `/access-request/${id}/unlock`,
        method: "POST",
        body,
      }),
      invalidatesTags: [Tags.UNLOCK_ELIGIBLE, Tags.ACCESS_REQUEST],
    }),
    validateKonvivaTraining: builder.query<
      { approved: boolean; pendingCourses: PendingCourse[] },
      string
    >({
      query: (cpf) => ({
        url: `/konviva/validate/${cpf}`,
        method: "GET",
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetAccessRequestQuery,
  useCreateRequestMutation,
  useGetByIdQuery,
  useUpdatedRequestMutation,
  useFinishRequestMutation,
  useValidateKonvivaTrainingQuery,
  useGetUnlockStoresQuery,
  useGetUnlockEligibleQuery,
  useCreateUnlockRequestMutation,
  useApproveUnlockRequestMutation,
} = requestAccess;
